/**
 * Vorlage „Kalt stellen“.
 *
 * Die Kühlschranktür öffnet sich, eine abgedeckte Schüssel wandert auf ein
 * Fach, die Tür schließt. Eine Schneeflocke und die Uhr zeigen, wie lange
 * es kalt stehen muss.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path, polyline, roundedRectPath } from '../paths.ts';
import { compact, faded, group, line, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { arrow, BADGE_RIGHT, counter, durationLabel, GROUND_Y, timerBadge } from '../parts/stage.ts';
import { bowl, contents } from '../parts/vessels.ts';
import { minutesFromText } from '../inference.ts';
import { defineTemplate, numberValue } from '../template.ts';

/** Kühlschrank in der Vorderansicht, Scharnier rechts. */
const FRIDGE = { left: 22, right: 122, top: 14, bottom: GROUND_Y } as const;
const FRIDGE_WIDTH = FRIDGE.right - FRIDGE.left;
/** Fächer innen. */
const SHELVES = [56, 96] as const;
/** Schüssel: Platz auf der Arbeitsfläche und im Fach. */
const OUTSIDE = { x: 190, rim: GROUND_Y - 20 } as const;
const INSIDE = { x: 70, rim: SHELVES[1] - 20 } as const;

/** Zeitplan. */
const TIME = {
  open: [0.04, 0.16],
  lift: [0.16, 0.26],
  slide: [0.26, 0.38],
  close: [0.4, 0.52],
  cold: [0.52, 0.86],
  reset: [0.9, 1],
} as const;

/** Schüssel mit Folie, Rand bei rim. */
function coveredBowl(cx: number, rim: number): SceneNode[] {
  const vessel = bowl(cx, rim, 46, 20);
  return [
    ...vessel.back,
    contents(vessel, 0.3, 'tint'),
    ...vessel.front,
    // Frischhaltefolie spannt über den Rand
    shape(
      path()
        .move(cx - 25, rim + 1)
        .quad(cx, rim - 8, cx + 25, rim + 1)
        .toString(),
      { stroke: 'deep', strokeWidth: STROKE.fine },
    ),
  ];
}

/** Schneeflocke mit sechs Armen. */
function snowflake(cx: number, cy: number, r: number): SceneNode {
  const arms: SceneNode[] = [];
  for (let index = 0; index < 6; index += 1) {
    const angle = (index / 6) * Math.PI * 2;
    const ex = cx + Math.cos(angle) * r;
    const ey = cy + Math.sin(angle) * r;
    arms.push(line(cx, cy, ex, ey, { stroke: 'ink', strokeWidth: STROKE.detail }));
    const bx = cx + Math.cos(angle) * r * 0.6;
    const by = cy + Math.sin(angle) * r * 0.6;
    for (const side of [-1, 1]) {
      const branch = angle + side * 0.7;
      arms.push(
        line(bx, by, bx + Math.cos(branch) * r * 0.32, by + Math.sin(branch) * r * 0.32, {
          stroke: 'ink',
          strokeWidth: STROKE.detail,
        }),
      );
    }
  }
  return group(arms);
}

/**
 * Tür, um das Scharnier rechts gedreht: 0 geschlossen, 1 weit offen. Ab einem
 * Viertelkreis steht sie nach rechts aus dem Gehäuse heraus.
 */
function door(open: number): SceneNode[] {
  const angle = open * 105;
  const radians = (angle * Math.PI) / 180;
  const edgeX = FRIDGE.right - FRIDGE_WIDTH * Math.cos(radians);
  const skew = 10 * Math.sin(radians);
  const outline = polyline(
    [
      { x: FRIDGE.right, y: FRIDGE.top },
      { x: edgeX, y: FRIDGE.top - skew * 0.6 },
      { x: edgeX, y: FRIDGE.bottom - 4 + skew * 0.6 },
      { x: FRIDGE.right, y: FRIDGE.bottom - 4 },
    ],
    true,
  );
  const handleX = lerp(edgeX, FRIDGE.right, 0.12);
  return compact([
    shape(outline, styles.solid(angle > 90 ? 'tint' : 'paper')),
    angle < 80 ? rect(handleX - 2, 44, 4, 30, 2, styles.solid('ink')) : null,
    angle < 30 ? line(FRIDGE.left + 2, 50, edgeX - 2, 50, styles.fine('shade')) : null,
  ]);
}

/** Vorlage „Kalt stellen“. */
export const prepChillTemplate = defineTemplate({
  id: 'prep.chill',
  group: 'prep',
  title: 'Kalt stellen',
  description:
    'Die abgedeckte Schüssel kommt in den Kühlschrank und bleibt dort, bis die Masse fest und durchgekühlt ist.',
  durationSeconds: 6,
  keywords: [
    'kalt stellen',
    'kaltstellen',
    'kühl stellen',
    'kühlstellen',
    'kühlschrank',
    'kühlen',
    'gekühlt',
    'fest werden',
    'durchkühlen',
  ],
  stillTime: 0.7,
  parameters: [
    {
      kind: 'number',
      name: 'minutes',
      label: 'Dauer',
      min: 10,
      max: 1440,
      step: 10,
      unit: 'Min',
      defaultValue: 120,
    },
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    if (lower.includes('über nacht')) return { minutes: 720 };
    return { minutes: minutesFromText(text) };
  },
  scene(values, t) {
    const minutes = numberValue(values, 'minutes', 120);
    const open = ease.inOutCubic(segment(t, ...TIME.open)) - ease.inOutCubic(segment(t, ...TIME.close));
    const lift = ease.inOutCubic(segment(t, ...TIME.lift));
    const slide = ease.inOutCubic(segment(t, ...TIME.slide));
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const cold = segment(t, ...TIME.cold);
    const coldVisible = Math.min(1, cold * 6) * (1 - segment(t, 0.84, 0.88));

    // Die Schüssel wird angehoben und ins Fach geschoben.
    const moving = t >= TIME.lift[0] && t < TIME.close[0];
    // Nach dem Schließen verdeckt die Tür die Schüssel; ab dem Rücksetzen wird sie nicht mehr gezeichnet.
    const inside = t >= TIME.slide[1] && t < TIME.reset[0];
    const cx = lerp(OUTSIDE.x, INSIDE.x, slide);
    const rim = lerp(OUTSIDE.rim, INSIDE.rim, lift);
    // Am Ende steht wieder eine Schüssel bereit; sie wird eingeblendet.
    const waiting = t < TIME.lift[0] ? 1 : reset;

    const body: SceneNode[] = [
      shape(
        roundedRectPath(FRIDGE.left, FRIDGE.top, FRIDGE_WIDTH, FRIDGE.bottom - FRIDGE.top - 4, 6),
        styles.solid('paper'),
      ),
      shape(
        roundedRectPath(
          FRIDGE.left + 6,
          FRIDGE.top + 6,
          FRIDGE_WIDTH - 12,
          FRIDGE.bottom - FRIDGE.top - 16,
          3,
        ),
        styles.solid('tint'),
      ),
      ...SHELVES.map((y) => line(FRIDGE.left + 6, y, FRIDGE.right - 6, y, styles.detail('deep'))),
      rect(FRIDGE.left + 8, FRIDGE.bottom - 4, 12, 4, 1.5, styles.flat('ink')),
      rect(FRIDGE.right - 20, FRIDGE.bottom - 4, 12, 4, 1.5, styles.flat('ink')),
    ];
    const bowlNode = moving || inside ? group(coveredBowl(cx, rim)) : null;
    const doorNodes = door(open);

    return compact([
      ...counter(),
      ...body,
      // Solange die Tür offen ist, liegt die Schüssel davor; danach dahinter.
      ...(t < TIME.close[0] ? [...doorNodes, bowlNode] : [bowlNode, ...doorNodes]),
      waiting > 0.001 ? faded(waiting, coveredBowl(OUTSIDE.x, OUTSIDE.rim)) : null,
      faded(coldVisible, [snowflake(FRIDGE.left + FRIDGE_WIDTH / 2, 30, 10)]),
      faded(coldVisible, [timerBadge(BADGE_RIGHT, 22, durationLabel(minutes), cold)]),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: OUTSIDE.x, y: OUTSIDE.rim - 16 },
        { x: (OUTSIDE.x + INSIDE.x) / 2 + 20, y: INSIDE.rim - 30 },
        { x: INSIDE.x + 20, y: INSIDE.rim - 10 },
      ]),
    ];
  },
});
